import { SeatStepper, formatMoney } from "../components";

const BILLING_CYCLE_MAP = {
  monthly:   { period: 1, period_unit: "month" },
  quarterly: { period: 3, period_unit: "month" },
  annually:  { period: 1, period_unit: "year" },
};

const PERIOD_LABEL = {
  monthly:   "/mo",
  quarterly: "/qtr",
  annually:  "/yr",
};

function findMatchingPrice(plan_prices, billingCycle) {
  if (!plan_prices || !plan_prices.length) return null;
  const { period, period_unit } = BILLING_CYCLE_MAP[billingCycle] || BILLING_CYCLE_MAP.monthly;
  return plan_prices.find((p) => p.period === period && p.period_unit === period_unit) || null;
}

const labelCls = "block text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-1.5";
const cellCls  = "h-9 flex items-center px-3 bg-white border border-gray-100 rounded-lg";

function PriceInput({ value, onChange, label }) {
  return (
    <div>
      <p className={labelCls}>{label}</p>
      <div className="relative h-9">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
          $
        </span>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full h-full pl-6 pr-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
      </div>
    </div>
  );
}

function AddonDetail({ qty, unitPrice, isPerUnit, periodLabel, onQtyChange, onPriceChange }) {
  const total = isPerUnit ? unitPrice * qty : unitPrice;

  if (isPerUnit) {
    return (
      <div className="grid grid-cols-3 gap-3">
        <div>
          <p className={labelCls}>QTY</p>
          <SeatStepper value={qty} onChange={onQtyChange} min={1} />
        </div>
        <div>
          <PriceInput label="Price/Seat" value={unitPrice} onChange={onPriceChange} />
          <p className="text-[11px] text-gray-400 mt-1 text-right">{periodLabel}</p>
        </div>
        <div>
          <p className={labelCls}>Total</p>
          <div className={cellCls + " justify-end"}>
            <span className="text-sm font-bold text-gray-900">${formatMoney(total)}</span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1 text-right">{periodLabel}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <PriceInput label="Price" value={unitPrice} onChange={onPriceChange} />
        <p className="text-[11px] text-gray-400 mt-1 text-right">{periodLabel}</p>
      </div>
      <div>
        <p className={labelCls}>Total</p>
        <div className={cellCls + " justify-end"}>
          <span className="text-sm font-bold text-gray-900">${formatMoney(total)}</span>
        </div>
        <p className="text-[11px] text-gray-400 mt-1 text-right">{periodLabel}</p>
      </div>
    </div>
  );
}

function EditAddOns({
  availableAddons,
  checkedAddons,
  onToggle,
  billingCycle,
  seats,
  searchQuery,
  onSearchChange,
  addonOverrides,
  onAddonOverrideChange,
  planFamilyId,
}) {
  const selectedCount = checkedAddons.size;

  const totalAmount = [...checkedAddons].reduce((sum, name) => {
    const addon = availableAddons.find((a) => a.plan_name === name);
    if (!addon) return sum;
    if (planFamilyId && addon.plan_family_id !== planFamilyId) return sum;
    const p = findMatchingPrice(addon.plan_prices, billingCycle);
    if (!p) return sum;
    const override = addonOverrides[name] || {};
    const unitPrice = override.unitPrice ?? p.price;
    const qty = override.qty ?? (p.pricing_model === "per_unit" ? seats : 1);
    return sum + (p.pricing_model === "per_unit" ? unitPrice * qty : unitPrice);
  }, 0);

  const filtered = availableAddons.filter((addon) => {
    if (planFamilyId && addon.plan_family_id !== planFamilyId) return false;
    if (!findMatchingPrice(addon.plan_prices, billingCycle)) return false;
    return !searchQuery || addon.plan_name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">

      {/* Header */}
      <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Add-ons</h3>
        <span className="text-xs text-gray-600 font-medium">
          {selectedCount} selected · ${formatMoney(totalAmount)}{PERIOD_LABEL[billingCycle] ?? "/mo"}
        </span>
      </div>

      {/* Search */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="relative">
          <svg
            xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            aria-hidden="true"
          >
            <path d="m21 21-4.34-4.34" /><circle cx="11" cy="11" r="8" />
          </svg>
          <input
            type="text"
            placeholder="Search add-ons..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full h-8 pl-8 pr-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 focus:bg-white"
          />
        </div>
      </div>

      {/* Addon list */}
      <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
        {filtered.length === 0 && (
          <p className="px-4 py-6 text-sm text-gray-400 text-center">No add-ons found.</p>
        )}

        {filtered.map((addon) => {
          const matchingPrice = findMatchingPrice(addon.plan_prices, billingCycle);
          const isChecked   = checkedAddons.has(addon.plan_name);
          const isPerUnit   = matchingPrice.pricing_model === "per_unit";
          const periodLabel = PERIOD_LABEL[billingCycle] ?? "/mo";

          const override  = addonOverrides[addon.plan_name] || {};
          const qty       = override.qty ?? (isPerUnit ? seats : 1);
          const unitPrice = override.unitPrice ?? matchingPrice.price;
          const rowPrice  = isPerUnit ? unitPrice * qty : unitPrice;

          return (
            <div key={addon.plan_name}>

              {/* Checkbox row */}
              <div
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                  isChecked ? "bg-blue-50/40" : "hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  id={`addon-${addon.plan_name}`}
                  checked={isChecked}
                  onChange={() => onToggle(addon.plan_name)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                />
                <label
                  htmlFor={`addon-${addon.plan_name}`}
                  className="flex-1 text-sm min-w-0 text-gray-900 cursor-pointer"
                >
                  {addon.plan_name}
                </label>
                <span className="text-sm font-medium flex-shrink-0 text-gray-700">
                  ${formatMoney(rowPrice)}{periodLabel}
                </span>
              </div>

              {/* Expanded editable detail when checked */}
              {isChecked && (
                <div className="px-4 pb-3 bg-blue-50/40">
                  <div className="ml-7 p-3 bg-gray-50 border border-gray-100 rounded-lg">
                    <AddonDetail
                      qty={qty}
                      unitPrice={unitPrice}
                      isPerUnit={isPerUnit}
                      periodLabel={periodLabel}
                      onQtyChange={(val) => onAddonOverrideChange(addon.plan_name, "qty", val)}
                      onPriceChange={(val) => onAddonOverrideChange(addon.plan_name, "unitPrice", val)}
                    />
                  </div>
                </div>
              )}

            </div>
          );
        })}
      </div>

    </section>
  );
}

export default EditAddOns;
